import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import { PrimaryButton } from "../Buttons";

/**
 * Reusable Modal component with mobile-friendly bottom sheet and desktop centered layouts
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal should close
 * @param {string} title - Modal title
 * @param {string} subtitle - Optional subtitle below title
 * @param {ReactNode} children - Modal content
 * @param {ReactNode} footer - Optional footer content (defaults to Close button if not provided)
 * @param {string} maxWidth - Maximum width on desktop (default: "max-w-md")
 * @param {boolean} showCloseButton - Whether to show close button in header (default: true)
 * @param {string} className - Additional classes for modal container
 * @param {string} bodyClassName - Additional classes for the scrollable body
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  maxWidth = "max-w-md",
  showCloseButton = true,
  className = "",
  bodyClassName = "",
}) => {
  // Add CSS animation on mount
  useEffect(() => {
    if (typeof document !== "undefined") {
      const styleId = "modal-animation";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          @keyframes slide-up {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
          @media (min-width: 640px) {
            .modal-desktop {
              animation: none !important;
            }
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={`fixed inset-x-0 bottom-0 sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 
                   bg-white rounded-t-lg sm:rounded-lg shadow-xl z-50 
                   max-h-[85vh] sm:max-h-[80vh] w-full sm:w-full ${maxWidth}
                   flex flex-col modal-desktop ${className}`}
        style={{
          animation: "slide-up 0.3s ease-out",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
      >
        {/* Header */}
        {(title || subtitle || showCloseButton) && (
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-brand-charcoal/10">
            <div>
              {title && (
                <h2
                  id="modal-title"
                  className="heading-display text-lg sm:text-xl"
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-sm text-brand-muted mt-1">{subtitle}</p>
              )}
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className="inline-flex items-center justify-center min-h-11 min-w-11 hover:bg-brand-cream rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <FaTimes className="w-5 h-5 text-brand-muted" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-4 sm:p-6 min-h-0 ${bodyClassName}`}>
          {children}
        </div>

        {/* Footer */}
        {footer !== null && (
          <div className="p-4 sm:p-6 border-t border-brand-charcoal/10">
            {footer || (
              <PrimaryButton onClick={onClose} className="w-full">
                Close
              </PrimaryButton>
            )}
          </div>
        )}
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;

